import { Alert, Stack, TextField, Typography } from "@mui/material"
import type { BiomeshContactResponse } from "@phc/common/gen/threatAnalyticsGen"
import {
  getListContactsBiomeshContactListGetQueryKey,
  useCreateContactBiomeshContactPost,
  useUpdateContactBiomeshContactContactIdPut,
} from "@phc/common/gen/threatAnalyticsGen"
import { useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import type { FC } from "react"
import { useCallback, useState } from "react"
import { extraColors } from "../../utils/theme"
import { Modal } from "../Shared/Modal"
import { extractApiError } from "./copUtils"

interface ContactFormDialogProps {
  open: boolean
  onClose: () => void
  contact?: BiomeshContactResponse
  /** Called with the new/updated contact ID after a successful save */
  onSaved?: (contactId: string) => void
}

export const ContactFormDialog: FC<ContactFormDialogProps> = ({
  open,
  onClose,
  contact,
  onSaved,
}) => {
  const isEdit = !!contact
  const queryClient = useQueryClient()

  const existingName = contact
    ? `${contact.first_name} ${contact.last_name}`
    : ""
  const [name, setName] = useState(existingName)
  const [rank, setRank] = useState(contact?.rank ?? "")
  const [email, setEmail] = useState(contact?.email ?? "")
  const [phone, setPhone] = useState(contact?.phone ?? "")
  const [formError, setFormError] = useState("")

  const { mutateAsync: createContact, isPending: createPending } =
    useCreateContactBiomeshContactPost()
  const { mutateAsync: updateContact, isPending: updatePending } =
    useUpdateContactBiomeshContactContactIdPut()

  const isPending = createPending || updatePending

  const handleSave = useCallback(async () => {
    setFormError("")

    const trimmed = name.trim()
    if (!trimmed) {
      setFormError("Name is required")
      return
    }

    const spaceIdx = trimmed.indexOf(" ")
    const firstName = spaceIdx > 0 ? trimmed.slice(0, spaceIdx) : trimmed
    const lastName = spaceIdx > 0 ? trimmed.slice(spaceIdx + 1) : ""
    if (!lastName) {
      setFormError("Please enter both first and last name")
      return
    }

    const payload = {
      first_name: firstName,
      last_name: lastName,
      rank: rank.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
    }

    try {
      if (contact) {
        await updateContact({
          contactId: contact.contact_id,
          data: payload,
        })
        enqueueSnackbar("Contact updated", { variant: "success" })
        onSaved?.(contact.contact_id)
      } else {
        const result = await createContact({ data: payload })
        enqueueSnackbar("Contact created", { variant: "success" })
        onSaved?.(result.contact_id)
      }
      await queryClient.invalidateQueries({
        queryKey: getListContactsBiomeshContactListGetQueryKey(),
      })
      onClose()
    } catch (err) {
      setFormError(extractApiError(err, "Failed to save contact"))
    }
  }, [
    name,
    rank,
    email,
    phone,
    contact,
    createContact,
    updateContact,
    queryClient,
    onClose,
    onSaved,
  ])

  return (
    <Modal
      open={open}
      onClose={onClose}
      onConfirm={() => void handleSave()}
      title={isEdit ? "Edit Contact" : "New Primary POC"}
      confirmLabel="Save"
      disableBtns={isPending}
    >
      <Stack spacing={1.5}>
        <TextField
          label="Name"
          required
          fullWidth
          helperText="Full name first then last"
          value={name}
          onChange={e => setName(e.target.value)}
        />

        <TextField
          label="Rank"
          fullWidth
          value={rank}
          onChange={e => setRank(e.target.value)}
        />

        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          Contact <span style={{ color: extraColors.status.redLight }}>*</span>
        </Typography>

        <TextField
          label="Email"
          type="email"
          fullWidth
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <TextField
          label="Phone"
          type="tel"
          fullWidth
          value={phone}
          onChange={e => setPhone(e.target.value)}
        />
        {formError && <Alert severity="error">{formError}</Alert>}
      </Stack>
    </Modal>
  )
}
